import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FiMapPin,
  FiPhone,
  FiMail,
  FiClock,
  FiSend,
  FiCheck,
  FiAlertCircle,
} from "react-icons/fi";
import { FiFacebook, FiTwitter, FiInstagram, FiYoutube } from "react-icons/fi";
import { toast } from "react-toastify";
import { contactAPI } from "../services/api";

const initialForm = { name: "", phone: "", email: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.phone.trim()) newErrors.phone = "Phone number is required";
    if (!form.message.trim()) newErrors.message = "Message is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await contactAPI.submit({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      setSubmitted(true);
      setForm(initialForm);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Failed to send message. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (name) =>
    `w-full px-4 py-3 border ${errors[name] ? "border-red-400 bg-red-50" : "border-gray-200"} rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 transition-colors`;

  const ErrorMsg = ({ name }) =>
    errors[name] ? (
      <div className="flex items-center gap-1 mt-1 text-red-500 text-xs">
        <FiAlertCircle className="w-3 h-3" />
        {errors[name]}
      </div>
    ) : null;

  return (
    <div>
      {/* Hero */}
      <div className="hero-gradient py-24 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, #bfdbfe 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <p className="text-secondary-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Get in Touch
          </p>
          <h1 className="text-5xl font-serif font-bold text-white mb-4">
            Contact Us
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            We'd love to hear from you. Reach out to us with any questions.
          </p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-serif font-bold text-primary-500 mb-6">
              Send Us a Message
            </h2>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center"
              >
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCheck className="w-7 h-7 text-green-500" />
                </div>
                <h3 className="font-bold text-green-700 text-lg mb-2">
                  Message Sent!
                </h3>
                <p className="text-green-600 text-sm mb-4">
                  Thank you for contacting us. We will get back to you within 24
                  hours.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-5 py-2 bg-green-500 text-white rounded-xl text-sm font-medium hover:bg-green-600 transition-colors"
                >
                  Send Another
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className={inputClass("name")}
                  />
                  <ErrorMsg name="name" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <FiPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+977-9XXXXXXXXX"
                      className={`${inputClass("phone")} pl-10`}
                    />
                  </div>
                  <ErrorMsg name="phone" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address{" "}
                    <span className="text-gray-400 font-normal">
                      (optional)
                    </span>
                  </label>
                  <div className="relative">
                    <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                      className={`${inputClass("email")} pl-10`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Write your message here..."
                    className={`${inputClass("message")} resize-none`}
                  />
                  <ErrorMsg name="message" />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FiSend className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="text-3xl font-serif font-bold text-primary-500 mb-6">
              Contact Information
            </h2>

            {[
              {
                icon: FiMapPin,
                title: "Our Address",
                lines: [
                  "Buddha Deep English Boarding Secondary School",
                  "Kanchan Rural Municipality, Ward No. 5",
                  "Rupandehi District, Lumbini Province, Nepal",
                ],
              },
              {
                icon: FiPhone,
                title: "Phone Numbers",
                lines: [
                  "+977-71-XXXXXX (Main)",
                  "+977-98XXXXXXXX (Mobile)",
                ],
              },
              {
                icon: FiMail,
                title: "Email Addresses",
                lines: [
                  "buddhadeep2058@gmail.com",
                ],
              },
              {
                icon: FiClock,
                title: "Office Hours",
                lines: [
                  "Sunday - Friday: 7:00 AM - 5:00 PM",
                  "Saturday: 9:00 AM - 1:00 PM",
                  "Holidays: Closed",
                ],
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="flex gap-4 bg-gray-50 border border-gray-100 rounded-2xl p-5"
              >
                <div className="w-11 h-11 bg-primary-50 border border-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-primary-500" />
                </div>
                <div>
                  <h3 className="font-bold text-primary-500 text-sm mb-1">
                    {item.title}
                  </h3>
                  {item.lines.map((line, lIdx) => (
                    <p key={lIdx} className="text-gray-600 text-sm">
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            ))}

            {/* Social Media */}
            <div className="bg-primary-500 rounded-2xl p-5">
              <h3 className="font-bold text-white text-sm mb-4">Follow Us</h3>
              <div className="flex gap-3">
                {[
                  { icon: FiFacebook, label: "Facebook" },
                  { icon: FiTwitter, label: "Twitter" },
                  { icon: FiInstagram, label: "Instagram" },
                  { icon: FiYoutube, label: "YouTube" },
                ].map((social, idx) => (
                  <a
                    key={idx}
                    href="#"
                    aria-label={social.label}
                    className="w-10 h-10 bg-white/10 hover:bg-secondary-500 rounded-xl flex items-center justify-center text-white transition-colors"
                  >
                    <social.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Map Placeholder */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-14"
        >
          <h2 className="text-2xl font-serif font-bold text-primary-500 mb-5">
            Find Us
          </h2>
          <div className="w-full h-72 bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-100 rounded-3xl flex items-center justify-center overflow-hidden">
            <div className="text-center text-primary-300">
              <FiMapPin className="w-12 h-12 mx-auto mb-3 text-primary-400" />
              <p className="font-medium text-primary-500">
                Buddha Deep English Boarding Secondary School
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Kanchan Rural Municipality, Ward No. 5, Rupandehi, Nepal
              </p>
              <a
                href="https://maps.google.com/?q=Kanchan+Rural+Municipality+Rupandehi+Nepal"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 transition-colors"
              >
                <FiMapPin className="w-4 h-4" />
                Open in Google Maps
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
